import { type ChangeEventHandler, useState, type FC, useEffect } from 'react';
import styles from './setting-rpc-content.module.scss';
import { config } from '@config';
import { globalSettingModel, type ParrotCurrentRpcNode } from '@models/global-setting.model';
import { useModel } from 'foca';
import checkboxIcon from '@assets/images/parrot/checkbox.svg';
import checkboxSelectedIcon from '@assets/images/parrot/checkbox-selected.svg';
import { toast } from 'react-toastify';
import { startCase } from 'lodash-es';
import { SuiClient } from '@mysten/sui/client';
import { useToggle } from 'react-use';

const storageKey = 'Parrot_Current_RPC';
const rpcKeys: (keyof typeof config.rpc)[] = Object.keys(config.rpc);
const cacheRpc = localStorage.getItem(storageKey);

interface OwnProps {
  toggleSelecting(): void;
  visible: boolean;
}

const SettingRpcContent: FC<OwnProps> = ({ toggleSelecting, visible }) => {
  const rpcKey = useModel(globalSettingModel, (state) => state.rpcKey);
  const [customRpc, setCustomRpc] = useState(
    cacheRpc && (JSON.parse(cacheRpc) as ParrotCurrentRpcNode).rpcKey === 'custom'
      ? (JSON.parse(cacheRpc) as ParrotCurrentRpcNode).rpc
      : '',
  );
  const [isCustom, toggleIsCustom] = useToggle(rpcKey === 'custom' ? true : false);

  useEffect(() => {
    if (rpcKey !== 'custom' && visible) {
      toggleIsCustom(false);
    }
  }, [visible, rpcKey]);

  const changeRpc = async (key: ParrotCurrentRpcNode['rpcKey']) => {
    if (key !== 'custom' && !config.rpc[key]) {
      toast(
        <div style={{ color: 'red' }}>
          The current node is temporarily unavailable. Please switch to an alternative node to
          ensure uninterrupted service.
        </div>,
        { type: 'error' },
      );
      return;
    }

    if (key !== 'custom') {
      globalSettingModel.setRpcKey(key);

      toggleSelecting();
      localStorage.setItem(storageKey, JSON.stringify({ rpcKey: key, rpc: config.rpc[key] }));
      toast(`The current node updated! The current node: ${config.rpc[key]}`);
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  };

  const changeCustomRpc = (key: ParrotCurrentRpcNode['rpcKey']) => {
    toggleIsCustom(key === 'custom');
  };

  const setCustom: ChangeEventHandler<HTMLInputElement> = (e) => {
    setCustomRpc(e.target.value);
  };

  const saveCustom = async () => {
    if (!customRpc) {
      return;
    }
    globalSettingModel.setRpcKey('custom');

    toggleSelecting();
    localStorage.setItem(storageKey, JSON.stringify({ rpcKey: 'custom', rpc: customRpc }));
    toast(`The current node updated! The current node: ${customRpc}`);

    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapper_pad}>
        <div className={styles.options}>
          {rpcKeys.map((key) => (
            <List key={key} changeRpc={changeRpc} item={key} value={isCustom ? 'custom' : rpcKey} />
          ))}

          <List changeRpc={changeCustomRpc} item={'custom'} value={isCustom ? 'custom' : rpcKey} />
        </div>
        {isCustom ? (
          <div className={styles.custom}>
            <input
              type={'text'}
              placeholder={'Custom RPC URL'}
              onChange={setCustom}
              value={customRpc}
            />
            <button onClick={saveCustom}>Save</button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

interface ListOwnProps {
  changeRpc(value: string): void;
  item: string;
  value: string;
}

const List: FC<ListOwnProps> = ({ changeRpc, item, value }) => {
  const [time, setTime] = useState<number>();

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    const rpc = config.rpc[item];
    if (rpc) {
      const client = new SuiClient({ url: rpc });
      const loop = () => {
        const now = Date.now();
        client
          .getCoinMetadata({ coinType: '0x2::sui::SUI' })
          .then(() => {
            const end = Date.now();
            setTime(end - now);
          })
          .catch(() => {
            setTime(undefined);
          })
          .finally(() => {
            timer = setTimeout(() => {
              loop();
            }, 60000);
          });
      };
      loop();
    }

    return () => {
      clearTimeout(timer);
    };
  }, [item]);

  return (
    <div className={styles.li} onClick={() => changeRpc(item)}>
      <img src={item === value ? checkboxSelectedIcon : checkboxIcon} data-icon />
      <span className={styles.name}>{startCase(item.replace(/\_/g, ' '))}</span>
      {item !== 'custom' && time && (
        <div className={`${styles.time} ${time <= 200 ? styles.green : styles.red}`}>
          <span></span>
          {time}ms
        </div>
      )}
    </div>
  );
};

export default SettingRpcContent;
